<template>
  <div class="full-report">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <el-form :inline="true" :model="form" label-width="80px" @submit.native.prevent>
        <div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;">
          <el-form-item label="筛选条件">
            <el-radio-group v-model="form.datePreset" size="small" @change="handlePresetChange">
              <el-radio-button label="today">今天</el-radio-button>
              <el-radio-button label="yesterday">昨天</el-radio-button>
              <el-radio-button label="last7">过去7天</el-radio-button>
              <el-radio-button label="last14">过去14天</el-radio-button>
              <el-radio-button label="last30">过去30天</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="时间范围" class="date-range-item">
            <el-date-picker
              v-model="form.dateRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              :default-time="['00:00:00', '23:59:59']"
              format="yyyy-MM-dd HH:mm:ss"
              value-format="yyyy-MM-dd HH:mm:ss"
              :clearable="false"
              style="width:380px;"
              @change="handleDateRangeChange"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="loading" @click="handleSearch" icon="el-icon-search">查询</el-button>
            <el-button type="info" plain @click="handleReset" :disabled="loading" icon="el-icon-refresh">重置</el-button>
          </el-form-item>
        </div>
      </el-form>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty
        v-else-if="!loading && allRecords.length === 0"
        description="所选时间范围内暂无报表数据"
      />

      <div v-else>
        <div class="tip-bar">
          bidding是竞价广告，int是插屏，banner是横幅，splash是开屏，rv是激励视频；符合「广告平台_广告类型_值」命名规则的记录可查看相关人员（_bidding 除外）
        </div>
        <el-table
          :data="tableData"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在查询..."
          class="animate__animated animate__fadeIn"
        >
          <el-table-column prop="adsourceName" label="广告源名称" min-width="260">
            <template slot-scope="{ row }">
              <div class="adsource-cell">
                <span class="adsource-name" :title="row.adsourceName">{{ row.adsourceName || '-' }}</span>
                <el-button
                  v-if="canShowRelated(row)"
                  type="warning"
                  size="mini"
                  plain
                  @click="openRelatedDialog(row)"
                >相关人员</el-button>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="adsourceId" label="广告源ID" min-width="110" align="center" />
          <el-table-column prop="slotId" label="广告位ID" min-width="150" show-overflow-tooltip />
          <el-table-column prop="estimateRevenue" label="预估收益" min-width="120" :formatter="numFormatter" />
          <el-table-column prop="revenue" label="真实收益" min-width="110" :formatter="numFormatter" />
          <el-table-column prop="estimateEcpm" label="预估eCPM" min-width="100" :formatter="numFormatter" />
          <el-table-column prop="ecpmApi" label="eCPM API" min-width="100" :formatter="numFormatter" />
          <el-table-column prop="diffPercent" label="差额" min-width="100" align="center">
            <template slot-scope="{ row }">
              <span :class="diffClass(row)">{{ row.diffPercent }}%</span>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 相关人员弹窗 -->
    <el-dialog
      title="相关人员"
      :visible.sync="relatedDialogVisible"
      width="900px"
      append-to-body
      @close="handleRelatedDialogClose"
    >
      <div v-if="relatedFilter" class="related-filter-bar">
        <el-tag size="small" type="info">广告平台：{{ relatedFilter.adnName }}</el-tag>
        <el-tag size="small" type="info">广告类型：{{ adTypeLabel(relatedFilter.adType) }}</el-tag>
        <el-tag size="small" type="info">筛选用收入：{{ relatedFilter.incomeDisplay }}（名称eCPM÷1000）</el-tag>
        <el-tag size="small" type="info">时间：{{ relatedFilter.startTime }} ~ {{ relatedFilter.endTime }}</el-tag>
      </div>
      <div v-if="relatedError" class="error">{{ relatedError }}</div>
      <el-table
        :data="relatedRecords"
        border
        stripe
        v-loading="relatedLoading"
        element-loading-text="正在查询相关人员..."
        max-height="460"
      >
        <el-table-column prop="userId" label="用户ID" width="100" align="center" />
        <el-table-column prop="adnName" label="广告平台" min-width="110" />
        <el-table-column prop="adType" label="广告类型" min-width="90" :formatter="adTypeColFormatter" />
        <el-table-column prop="ecpm" label="收入" min-width="90" :formatter="ecpmFormatter" />
        <el-table-column prop="transId" label="交易ID" min-width="150" show-overflow-tooltip />
        <el-table-column prop="createdTime" label="创建时间" min-width="160" :formatter="timeFormatter" />
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-if="!bannedUserMap[row.userId]"
              type="danger"
              size="mini"
              @click="openBanDialog(row)"
            >封禁</el-button>
            <el-tag v-else type="info" size="small">已封禁</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-container">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :current-page="relatedPagination.page"
          :page-size="relatedPagination.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="relatedPagination.total"
          @current-change="handleRelatedPageChange"
          @size-change="handleRelatedSizeChange"
        />
      </div>
    </el-dialog>

    <!-- 封禁用户弹窗 -->
    <el-dialog
      title="封禁用户"
      :visible.sync="banDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="banFormRef" :model="banForm" :rules="banRules" label-width="100px">
        <el-form-item label="用户ID">
          <el-input :value="banUserId" disabled />
        </el-form-item>
        <el-form-item label="封禁描述" prop="banDes">
          <el-input
            v-model.trim="banForm.banDes"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请输入封禁原因"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="banDialogVisible = false">取消</el-button>
        <el-button type="danger" :loading="banSubmitting" @click="handleBanSubmit">确认封禁</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get, put } from '@/utils/http.js'

const AD_TYPE_SHORT_MAP = {
  rv: 'reward',
  int: 'interstitial',
  banner: 'banner',
  splash: 'splash'
}

const AD_TYPE_LABEL_MAP = {
  reward: '激励',
  interstitial: '插屏',
  banner: 'banner',
  splash: '开屏',
  native_feed: '信息流'
}

/** 解析广告源名称：SA_平台_类型_值 或 平台_类型_值；含 _bidding 则不可查相关人员 */
function parseAdsourceName(name) {
  if (!name || typeof name !== 'string') return null
  let raw = name.trim()
  if (!raw) return null
  if (/_bidding$/i.test(raw)) return null

  if (/^SA_/i.test(raw)) {
    raw = raw.replace(/^SA_/i, '')
  }

  const match = raw.match(/^(.+)_(rv|int|banner|splash)_([\d.]+)$/i)
  if (!match) return null

  const adnName = match[1]
  const shortType = match[2].toLowerCase()
  const adType = AD_TYPE_SHORT_MAP[shortType]
  if (!adnName || !adType) return null

  return {
    adnName,
    adType,
    estimateEcpmFromName: match[3]
  }
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function formatDateTime(date) {
  return [
    date.getFullYear(),
    pad2(date.getMonth() + 1),
    pad2(date.getDate())
  ].join('-') + ` ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
}

function toDateOnly(value) {
  return value ? String(value).slice(0, 10) : ''
}

function getDefaultForm() {
  return {
    datePreset: 'today',
    dateRange: resolveDateRange('today')
  }
}

function resolveDateRange(preset) {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
  const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)

  if (preset === 'today') {
    return [formatDateTime(todayStart), formatDateTime(todayEnd)]
  }

  if (preset === 'yesterday') {
    const start = new Date(todayStart)
    start.setDate(start.getDate() - 1)
    const end = new Date(todayEnd)
    end.setDate(end.getDate() - 1)
    return [formatDateTime(start), formatDateTime(end)]
  }

  const daysMap = { last7: 7, last14: 14, last30: 30 }
  const days = daysMap[preset]
  if (!days) {
    return [formatDateTime(todayStart), formatDateTime(todayEnd)]
  }

  const start = new Date(todayStart)
  start.setDate(start.getDate() - (days - 1))
  return [formatDateTime(start), formatDateTime(todayEnd)]
}

function parsePageData(data) {
  if (!data) {
    return { records: [], total: 0, page: 1, pageSize: 10 }
  }
  const records = data.records || data.list || data.rows || []
  return {
    records: Array.isArray(records) ? records : [],
    total: Number(data.total ?? data.totalCount ?? 0),
    page: Number(data.page ?? data.current ?? data.pageNum ?? 1),
    pageSize: Number(data.pageSize ?? data.size ?? 10)
  }
}

export default {
  name: 'FullReportManagement',
  data() {
    return {
      form: getDefaultForm(),
      loading: false,
      error: '',
      allRecords: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      relatedDialogVisible: false,
      relatedLoading: false,
      relatedError: '',
      relatedFilter: null,
      relatedRecords: [],
      relatedPagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      bannedUserMap: {},
      banDialogVisible: false,
      banUserId: null,
      banSubmitting: false,
      banForm: {
        banDes: ''
      },
      banRules: {
        banDes: [{ required: true, message: '请输入封禁描述', trigger: 'blur' }]
      }
    }
  },
  computed: {
    tableData() {
      const start = (this.pagination.page - 1) * this.pagination.pageSize
      return this.allRecords.slice(start, start + this.pagination.pageSize)
    }
  },
  mounted() {
    this.handleSearch()
  },
  methods: {
    numFormatter(row, column, cellValue) {
      if (cellValue === null || cellValue === undefined || cellValue === '') return '-'
      const n = Number(cellValue)
      if (Number.isNaN(n)) return cellValue
      return n.toFixed(4)
    },
    timeFormatter(row, column, cellValue) {
      if (!cellValue) return ''
      return String(cellValue).replace('T', ' ')
    },
    ecpmFormatter(row, column, cellValue) {
      return ((cellValue || 0) / 1000).toFixed(2)
    },
    adTypeColFormatter(row, column, cellValue) {
      return this.adTypeLabel(cellValue)
    },
    adTypeLabel(adType) {
      return AD_TYPE_LABEL_MAP[adType] || adType || ''
    },
    /** 页面展示单位（元）转库内 ecpm（1元=1000） */
    toEcpmUnit(displayVal) {
      if (displayVal === '' || displayVal == null) return null
      const n = Number(displayVal)
      if (Number.isNaN(n)) return null
      return Math.round(n * 1000)
    },
    canShowRelated(row) {
      return !!parseAdsourceName(row && row.adsourceName)
    },
    diffClass(row) {
      const n = Number(row.diffPercent)
      if (Number.isNaN(n)) return ''
      if (row.sortGroup === 0 || n === 100) return 'diff-zero'
      if (n < 0) return 'diff-neg'
      if (n > 0) return 'diff-pos'
      return ''
    },
    handlePresetChange(preset) {
      this.form.dateRange = resolveDateRange(preset)
      this.handleSearch()
    },
    handleDateRangeChange() {
      this.form.datePreset = ''
    },
    handleReset() {
      this.form = getDefaultForm()
      this.error = ''
      this.allRecords = []
      this.pagination = { page: 1, pageSize: 10, total: 0 }
      this.handleSearch()
    },
    handleCurrentChange(page) {
      this.pagination.page = page
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
    },
    async handleSearch() {
      this.pagination.page = 1
      await this.fetchData()
    },
    async fetchData() {
      this.error = ''
      this.loading = true
      try {
        const [start, end] = this.form.dateRange || []
        if (!start || !end) {
          throw new Error('请选择时间范围')
        }
        const startDate = toDateOnly(start)
        const endDate = toDateOnly(end)
        if (startDate > endDate) {
          throw new Error('结束日期不能早于开始日期')
        }
        const result = await get('/admin/fullReport/list', {
          startDate,
          endDate
        })
        const list = (result && result.data) ? result.data : []
        this.allRecords = Array.isArray(list) ? list : []
        this.pagination.total = this.allRecords.length
      } catch (e) {
        this.error = e && e.message ? e.message : '请求出现错误'
        this.allRecords = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },

    openRelatedDialog(row) {
      const parsed = parseAdsourceName(row.adsourceName)
      if (!parsed) {
        this.$message.warning('该广告源名称不符合筛选规则')
        return
      }
      const [startTime, endTime] = this.form.dateRange || []
      if (!startTime || !endTime) {
        this.$message.warning('请先选择时间范围')
        return
      }
      const nameEcpm = Number(parsed.estimateEcpmFromName)
      if (Number.isNaN(nameEcpm)) {
        this.$message.warning('名称中的预估eCPM无效，无法查询相关人员')
        return
      }
      // 名称中的预估eCPM ÷ 1000 作为广告收入页的「收入」筛选值
      const incomeYuan = nameEcpm / 1000
      const ecpm = this.toEcpmUnit(incomeYuan)
      if (ecpm == null) {
        this.$message.warning('预估eCPM格式不正确')
        return
      }

      this.relatedFilter = {
        adnName: parsed.adnName,
        adType: parsed.adType,
        nameEcpm,
        incomeYuan,
        incomeDisplay: incomeYuan.toFixed(4),
        ecpm,
        startTime,
        endTime
      }
      this.relatedPagination = { page: 1, pageSize: 10, total: 0 }
      this.relatedRecords = []
      this.relatedError = ''
      this.bannedUserMap = {}
      this.relatedDialogVisible = true
      this.fetchRelatedRecords()
    },

    handleRelatedDialogClose() {
      this.relatedFilter = null
      this.relatedRecords = []
      this.relatedError = ''
      this.relatedPagination = { page: 1, pageSize: 10, total: 0 }
    },

    handleRelatedPageChange(page) {
      this.relatedPagination.page = page
      this.fetchRelatedRecords()
    },
    handleRelatedSizeChange(size) {
      this.relatedPagination.pageSize = size
      this.relatedPagination.page = 1
      this.fetchRelatedRecords()
    },

    async fetchRelatedRecords() {
      if (!this.relatedFilter) return
      this.relatedLoading = true
      this.relatedError = ''
      try {
        const { adnName, adType, ecpm, startTime, endTime } = this.relatedFilter
        const result = await get('/admin/adRecord/page', {
          startTime,
          endTime,
          adnName,
          adType,
          ecpm,
          page: this.relatedPagination.page,
          pageSize: this.relatedPagination.pageSize
        })
        const page = parsePageData(result && result.data)
        this.relatedRecords = page.records
        this.relatedPagination.total = page.total
        this.relatedPagination.page = page.page || this.relatedPagination.page
        this.relatedPagination.pageSize = page.pageSize || this.relatedPagination.pageSize
      } catch (e) {
        this.relatedError = e && e.message ? e.message : '查询相关人员失败'
        this.relatedRecords = []
        this.relatedPagination.total = 0
      } finally {
        this.relatedLoading = false
      }
    },

    openBanDialog(row) {
      if (!row || !row.userId) {
        this.$message.warning('用户ID无效')
        return
      }
      this.banUserId = row.userId
      this.banForm.banDes = this.relatedFilter
        ? `报表相关人员：${this.relatedFilter.adnName}/${this.adTypeLabel(this.relatedFilter.adType)}/收入${this.relatedFilter.incomeDisplay}`
        : ''
      this.banDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.banFormRef) this.$refs.banFormRef.clearValidate()
      })
    },

    async handleBanSubmit() {
      try {
        await this.$refs.banFormRef.validate()
      } catch {
        return
      }
      if (!this.banUserId) return
      this.banSubmitting = true
      try {
        const res = await put(`/admin/users/${this.banUserId}`, {
          status: 1,
          banDes: this.banForm.banDes
        })
        this.$message.success(res.msg || '封禁成功')
        this.$set(this.bannedUserMap, this.banUserId, true)
        this.banDialogVisible = false
      } catch (e) {
        this.$message.error((e && e.message) || '封禁失败')
      } finally {
        this.banSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.full-report ::v-deep .el-table th,
.full-report ::v-deep .el-table th.is-leaf {
  background-color: #D6E4FF;
}

.date-range-item {
  position: relative;
}

.tip-bar {
  margin-bottom: 12px;
  font-size: 12px;
  color: #64748b;
}

.adsource-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.adsource-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.related-filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.error {
  color: #f87171;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  padding: 8px 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.diff-zero {
  color: #e6a23c;
  font-weight: 600;
}

.diff-neg {
  color: #f56c6c;
  font-weight: 600;
}

.diff-pos {
  color: #67c23a;
  font-weight: 600;
}

.el-card.animate__animated,
.el-table.animate__animated {
  --animate-duration: 0.3s;
}
</style>
